export const CHECKOUT_URL = 'https://pedimotor-en.impultienda.ar/checkout/';
export const MOCKUP_URL = 'https://cdn.magicpatterns.com/uploads/oyvTb2scCmRvkAKPFtAJMR/MOCKUP_PEDIMOTOR-EN.png';
export const AUTHOR_URL = 'https://cdn.magicpatterns.com/uploads/oPcfXnDAo7BNjCjBqVqtxM/DR._ELOWEN_VARELLI_-_SENSORY.png';

export const pathwayChips = ['Functional Findings', 'Priority Challenges', 'Measurable Goals', 'Activity Plans', 'Progress Tracking'];

export const scenarios = [
  'You observe a child struggling to sequence movements during dressing.',
  'Another child has difficulty organizing the steps required to complete a classroom activity.',
  'A third child can perform individual movements but struggles to coordinate them into a functional task.',
];

export const questions = [
  'Which observations are most relevant to functional participation?',
  "How should I organize the child's specific motor planning challenges?",
  'Which functional goals should take priority?',
  'How can I select activities that directly connect to those goals?',
  'How should I document my clinical reasoning?',
  'What should I track to determine whether the plan needs adjustment?',
];

export const beforeItems = [
  'Assessment observations spread across different notes.',
  'Difficulty identifying the most relevant functional challenges.',
  'Broad goals that are difficult to measure.',
  'Therapy activities selected without a clearly documented connection to goals.',
  "Session notes that don't consistently capture meaningful changes.",
  'Uncertainty about what to review when progress is limited.',
];

export const afterItems = [
  'Functional observations organized into clear categories.',
  'Priority challenges connected to everyday participation.',
  'Individualized goals with observable performance indicators.',
  'Activities selected through a structured planning matrix.',
  'Consistent documentation templates for each session.',
  'Progress tracking connected to the original functional goals.',
];

export const steps = [
  {
    verb: 'Organize',
    title: 'Turn observations into structured functional findings.',
    body: 'Use observation checklists and case organization worksheets to document relevant movement sequencing, task organization, and participation challenges.',
    deliverable: 'A structured functional observation profile.',
  },
  {
    verb: 'Prioritize',
    title: 'Identify the challenges that matter most to participation.',
    body: "Use a prioritization matrix to connect documented difficulties with the child's daily routines, functional needs, and individualized priorities.",
    deliverable: 'A functional challenge priority map.',
  },
  {
    verb: 'Plan',
    title: 'Convert priorities into individualized intervention goals.',
    body: 'Use goal-building worksheets to define observable targets, relevant task conditions, and practical measurement criteria.',
    deliverable: 'An individualized functional goal plan.',
  },
  {
    verb: 'Implement',
    title: 'Connect selected activities to documented goals.',
    body: 'Use an activity planning matrix to organize task demands, grading options, environmental considerations, and session objectives.',
    deliverable: 'A structured intervention activity plan.',
  },
  {
    verb: 'Track',
    title: 'Document sessions and review functional participation.',
    body: 'Use progress monitoring templates to record relevant observations, compare performance with established goals, and identify areas requiring professional reassessment.',
    deliverable: 'A documentation and progress tracking system.',
  },
];

export const volumes = [
  {
    title: 'Functional Motor Planning Observation Blueprint™',
    tagline: 'Turn everyday task observations into organized clinical information.',
    items: ['Functional Motor Planning Observation Checklist', 'Movement Sequencing Observation Worksheet', 'Task Initiation and Organization Record', 'Functional Participation Observation Matrix', 'Case Information Organization Template'],
    result: 'A structured observation profile that brings relevant functional findings into one organized document.',
  },
  {
    title: 'Motor Planning Challenge Prioritization Map™',
    tagline: 'Identify which documented challenges should guide your planning priorities.',
    items: ['Functional Challenge Classification Matrix', 'Daily Routine Participation Mapping Tool', 'Motor Planning Task Analysis Worksheet', 'Clinical Priority Organization Checklist', 'Case Prioritization Summary Template'],
    result: 'A documented priority map connecting functional observations with individualized planning needs.',
  },
  {
    title: 'Individualized Functional Goal Builder™',
    tagline: 'Transform relevant findings into measurable intervention goals.',
    items: ['Functional Goal Construction Worksheet', 'Observable Performance Indicator Checklist', 'Goal Prioritization Matrix', 'Short-Term and Long-Term Goal Planning Templates', 'Goal-to-Observation Alignment Checklist'],
    result: 'An individualized goal plan with documented targets and measurable performance criteria.',
  },
  {
    title: 'Motor Planning Activity Selection Matrix™',
    tagline: 'Connect intervention activities to the functional goals they are intended to address.',
    items: ['Functional Activity Selection Matrix', 'Movement Sequencing Activity Planner', 'Task Grading and Adaptation Worksheet', 'Activity-to-Goal Alignment Checklist', 'Session Activity Organization Template'],
    result: 'A case-specific activity plan showing how selected tasks connect to documented intervention goals.',
  },
  {
    title: 'Pediatric Intervention Session Planner™',
    tagline: 'Organize each session around clear objectives and documented clinical reasoning.',
    items: ['Session Planning Template', 'Functional Task Preparation Checklist', 'Session Observation Recording Sheet', 'Activity Adjustment Documentation Tool', 'Next-Session Planning Worksheet'],
    result: 'A repeatable session planning and documentation workflow.',
  },
  {
    title: 'Functional Participation Progress Tracker™',
    tagline: 'Track relevant changes and organize follow-up decisions.',
    items: ['Functional Goal Progress Tracking Sheet', 'Participation Observation Comparison Matrix', 'Session-to-Session Review Template', 'Intervention Plan Adjustment Checklist', 'Case Progress Summary Worksheet'],
    result: 'A structured progress monitoring document that supports ongoing professional review.',
  },
];

export const bonuses = [
  {
    title: 'Pediatric Motor Planning Quick-Reference Checklist™',
    description: 'A concise reference tool for reviewing the essential components of a motor planning case.',
    items: ['Case preparation checklist', 'Observation review prompts', 'Planning completeness checklist', 'Documentation review guide'],
    bestUsed: 'Preparing or reviewing a case before intervention planning.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/588a63d9-2982-4906-8846-8d52bb56b04b.jpg',
  },
  {
    title: 'Daily Living Task Analysis Toolkit™',
    description: 'A practical collection of task-analysis worksheets for organizing observations related to everyday pediatric activities.',
    items: ['Dressing task analysis worksheet', 'Self-care sequencing worksheet', 'Classroom participation task map', 'Multi-step functional activity breakdown'],
    bestUsed: 'Connecting motor planning observations with everyday participation demands.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/e70302c2-0c90-4c02-905e-ec78fff64375.jpg',
  },
  {
    title: 'Functional Goal Documentation Templates™',
    description: 'Ready-to-adapt templates for organizing individualized functional goals.',
    items: ['Goal documentation worksheet', 'Baseline performance record', 'Goal review template', 'Functional outcome summary sheet'],
    bestUsed: 'Preparing consistent, case-specific goal documentation.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/e78a7db0-68e6-4042-8d0e-9b5026a6792a.jpg',
  },
  {
    title: 'Parent & Caregiver Communication Planner™',
    description: 'Structured communication tools for organizing discussions about functional goals and participation priorities.',
    items: ['Caregiver priorities worksheet', 'Functional observation discussion guide', 'Home routine information form', 'Progress communication template'],
    bestUsed: 'Documenting relevant caregiver input and communicating the intervention plan.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/48ded92b-872b-4c4a-9873-28393850329e.jpg',
  },
  {
    title: 'Pediatric Motor Planning Case Review Dashboard™',
    description: 'A consolidated case-review tool that brings the major components of the pathway into one organized overview.',
    items: ['Observation summary', 'Priority challenge overview', 'Goal alignment review', 'Activity planning summary', 'Progress tracking overview'],
    bestUsed: 'Reviewing the overall consistency of an intervention plan.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/d8df32e8-8914-48c4-84b4-a9a8105606d1.jpg',
  },
];

export const caseSteps = [
  { title: 'First, you organize the observations.', body: 'You document task initiation, movement sequencing, environmental conditions, and the level of assistance observed.' },
  { title: 'Next, you identify functional priorities.', body: "You examine which documented difficulties interfere most with the child's participation in the dressing routine." },
  { title: 'Then, you structure the goals.', body: 'You define an individualized functional target, including the task conditions, observable performance, and criteria for review.' },
  { title: 'After that, you organize the activities.', body: 'You select and document appropriate activities based on the identified goals, task demands, and professional assessment.' },
  { title: 'Finally, you establish progress tracking.', body: "You record relevant session observations and review whether the plan remains aligned with the child's functional needs." },
];

export const caseResults = ['Functional Observation Profile', 'Priority Challenge Map', 'Individualized Goal Plan', 'Intervention Activity Plan', 'Progress Monitoring Record'];

export const organizeList = [
  'What you observed.',
  'Which functional challenges require attention.',
  'What your individualized goals are.',
  'How your planned activities connect to those goals.',
  "What information you'll document.",
  "How you'll review participation and progress.",
];

export const audiences = [
  { title: 'Pediatric Occupational Therapists', body: 'Organize functional observations, intervention goals, activity plans, and documentation.' },
  { title: 'Qualified Pediatric Rehabilitation Professionals', body: 'Use structured planning tools within their professional scope of practice.' },
  { title: 'Pediatric Therapy Teams', body: 'Support consistent organization of relevant case information and collaborative planning discussions.' },
  { title: 'Professionals Managing Complex Functional Participation Challenges', body: 'Create clearer connections between assessment findings and intervention planning.' },
];

export const testimonials = [
  {
    name: 'Isabel Rodríguez, OTR/L',
    role: 'Pediatric Occupational Therapist · Outpatient Clinic',
    quote: 'I used to finish evaluations with pages of notes and no clear thread. The priority map alone changed how I write goals — now every activity I plan points back to something I actually observed.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/2f6f5351-7fa3-41dc-9a3e-67cc24f051f0.jpg',
  },
  {
    name: 'Daniel Herrera, OT',
    role: 'Sensory Gym Clinician',
    quote: 'The activity selection matrix is what sold me. I can show parents and my team exactly why we chose each task and how we will know if it is working.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/b5b889fe-9c17-4627-b229-6e4e1758597a.jpg',
  },
  {
    name: 'Laura Méndez, MS, OTR',
    role: 'School-Based Occupational Therapist',
    quote: 'With a caseload of 40+ students, consistency is everything. Using the same five-part structure for every case has made my documentation faster and my progress reviews far more focused.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/09cefa78-7ba2-41f0-aaa6-17163d3ab43d.jpg',
  },
  {
    name: 'Camila Torres, OT',
    role: 'Early-Career Pediatric OT',
    quote: 'As a newer therapist, I needed a framework I could trust. The session planner and progress tracker gave me a repeatable workflow from day one. Worth far more than $27.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/946e0797-4b1d-4e32-a414-8ca52930c180.jpg',
  },
];

export const faqs = [
  { q: 'Is this a theoretical motor development textbook?', a: 'No. The Pediatric Motor Planning Blueprint™ focuses on practical planning tools, including checklists, matrices, worksheets, goal templates, session planners, and progress tracking resources.' },
  { q: 'Is this designed for occupational therapists?', a: 'Yes. The primary audience is pediatric occupational therapists and other qualified pediatric rehabilitation professionals working within their professional scope.' },
  { q: 'Does the blueprint provide specific treatment prescriptions?', a: 'No. It provides structured tools to support individualized clinical planning. Intervention decisions remain the responsibility of the qualified professional.' },
  { q: 'Can I use the templates with different pediatric cases?', a: 'The templates are designed to be adaptable to different functional participation challenges. Each application requires appropriate assessment, professional judgment, and individualization.' },
  { q: 'Does this replace standardized motor planning assessments?', a: 'No. The blueprint is a planning and documentation resource. It does not replace validated assessment instruments or establish a diagnosis.' },
  { q: 'What format will I receive?', a: "You'll receive digital access to the six practical volumes and five complementary bonuses, including the planning resources described in the offer." },
  { q: 'Can I use it to organize session documentation?', a: 'Yes. The package includes session planning, observation recording, goal review, and progress tracking templates.' },
  { q: 'Is this a monthly subscription?', a: 'No. The $27 purchase is a one-time payment.' },
  { q: 'How quickly will I receive access?', a: 'Digital access is provided after successful payment.' },
  { q: "What if the resource isn't right for me?", a: 'Your purchase includes a 7-day money-back guarantee.' },
];

export const recentBuyers = [
  { name: 'María Fernanda', country: 'Mexico' },
  { name: 'Javier', country: 'Spain' },
  { name: 'Valentina', country: 'Argentina' },
  { name: 'Andrés', country: 'Colombia' },
  { name: 'Sofía', country: 'Chile' },
  { name: 'Luis Alberto', country: 'Peru' },
  { name: 'Gabriela', country: 'Ecuador' },
  { name: 'Carolina', country: 'Uruguay' },
  { name: 'Diego', country: 'Costa Rica' },
  { name: 'Natalia', country: 'Guatemala' },
  { name: 'Ricardo', country: 'Dominican Republic' },
  { name: 'Paula', country: 'Venezuela' },
  { name: 'Mariana', country: 'Puerto Rico' },
  { name: 'Fernando', country: 'Bolivia' },
  { name: 'Lucía', country: 'Paraguay' },
];
